import propertyUseConst from '@/consts/propertyUse.const.js';
import _ from 'lodash';

export default {
  /* Get values from property Model */
  getFieldValueFromProperty(property, fieldName, featureSubIndex) {
    let value = property?.[fieldName];
    if (property?.[fieldName] === undefined) {
      value = this.getValueInAttributes(property, fieldName, featureSubIndex);
    }
    return value;
  },

  getFieldInAttributes(property, fieldToLookFor, FeatureSubIndex = null) {
    if (fieldToLookFor) {
      return property?.attributes?.find((x) => x.FeatureName?.toUpperCase() === fieldToLookFor.toUpperCase() && (FeatureSubIndex === undefined || FeatureSubIndex === null || FeatureSubIndex === x.FeatureSubIndex));
    } else {
      return null;
    }
  },

  // Receives property and field
  getValueInAttributes(property, fieldToLookFor, FeatureSubIndex = null) {
    return this.getValueInAttribute(this.getFieldInAttributes(property, fieldToLookFor, FeatureSubIndex));
  },

  // Receives field
  getValueInAttribute(attribute) {
    if (attribute !== null && attribute !== undefined) {
      if (attribute.IntegerValue !== null) {
        return attribute.IntegerValue;
      } else if (attribute.DecimalValue !== null) {
        return attribute.DecimalValue;
      } else if (attribute.StringValue !== null) {
        return attribute.StringValue;
      } else if (attribute.DateValue !== null) {
        return attribute.DateString || attribute.DateValue;
      } else if (attribute.BoolValue !== null) {
        return attribute.BoolValue;
      }
    } else {
      return null;
    }
  },

  /* returns undefined or an array with all the fields matching the property attributes
     (one if featureSubIndex === null or many if there are different featureSubIndexes)
  */
  getFieldFeatureSubindexesInAttributes(property, fieldToLookFor) {
    if (fieldToLookFor !== null && fieldToLookFor !== undefined) {
      return property?.attributes?.filter((x) => x.FeatureName.toUpperCase() === fieldToLookFor.toUpperCase());
    } else {
      return null;
    }
  },

  /* Set values from property Model */
  setValueInAttributes(property, field) {
    const attr = this.getFieldInAttributes(property, field.FeatureName, field.FeatureSubIndex);
    if (attr !== null && attr !== undefined) {
      attr.IntegerValue = field.IntegerValue;
      attr.DecimalValue = field.DecimalValue;
      attr.StringValue = field.StringValue;
      attr.DateValue = field.DateValue;
      attr.BoolValue = field.BoolValue;
      attr.FeatureSubIndex = field.FeatureSubIndex;
    } else {
      property.attributes.push(field);
    }
  },

  /* Set form values in property Model */
  setFormValuesInProperty(property, form, attributesMaster) {
    const { JobRowId, RowId } = property;
    const propertyChanges = {
      JobRowId,
      RowId,
      attributes: [],
    };

    form?.forEach(({ Editable, isReadOnly, ComputedOperation, ...control }) => {
      const controlIsReadonlyButAlsoComputed = isReadOnly && ComputedOperation;

      if (Editable && (!isReadOnly || controlIsReadonlyButAlsoComputed)) {
        this.setValueInProperty(property, propertyChanges, attributesMaster, control);
      }
    });
    return propertyChanges;
  },

  /* Set value in property Model */
  setValueInProperty(originalProperty, propertyChanges, attributesMaster, { InternalField, FieldDataType, Value, FeatureSubIndex }) {
    const attMaster = attributesMaster.find((att) => att.FeatureName === InternalField);

    if (!attMaster || !attMaster?.AttID) {
      propertyChanges[InternalField] = Value;
    } else {
      const dataTypeMapping = {
        bool: 'BoolValue',
        datetime: 'DateValue',
        decimal: 'DecimalValue',
        int: 'IntegerValue',
        string: 'StringValue',
      };
      const dataType = dataTypeMapping[FieldDataType];

      // Always cast property boolean attributes to true/false
      if (dataType === 'BoolValue' && Value !== null && Value !== undefined) {
        Value = Boolean(+Value);
      }

      let attr = this.getFieldInAttributes(originalProperty, InternalField, FeatureSubIndex);
      if (attr) {
        if (Value !== attr[dataType]) {
          attr[dataType] = Value;
        }
      } else {
        attr = {
          PropertyRowId: originalProperty.RowId,
          AttId: attMaster.AttID,
          FeatureName: InternalField,
          FeatureSubIndex,
          [dataType]: Value,
        };
      }
      propertyChanges.attributes.push(attr);
    }
  },

  // SubjectInfoPropertyType
  isMultiHousing(use) {
    return use === propertyUseConst.MULTIHOUSING;
  },
  isRetail(use) {
    return use === propertyUseConst.RETAIL;
  },
  isIndustrialLogistics(use) {
    return use === propertyUseConst.INDUSTRIAL;
  },
  isOffice(use) {
    return use === propertyUseConst.OFFICE;
  },
  isSpecial(use) {
    return use === propertyUseConst.SPECIAL;
  },
  isLandDevelopment(use) {
    return use === propertyUseConst.LAND;
  },
  isResidential(use) {
    return use === propertyUseConst.RESIDENTIAL;
  },
  isHospitality(use) {
    return use === propertyUseConst.HOSPITALITY;
  },
  isHealthcare(use) {
    return use === propertyUseConst.HEALTHCARE;
  },

  isMultifamilyType(use) {
    const multifamilyUses = [propertyUseConst.MULTIHOUSING, propertyUseConst.RESIDENTIAL, propertyUseConst.HOSPITALITY];
    return multifamilyUses.includes(use);
  },
  isCommercialType(use) {
    const commercialUses = [propertyUseConst.RETAIL, propertyUseConst.INDUSTRIAL, propertyUseConst.OFFICE, propertyUseConst.SPECIAL, propertyUseConst.LAND, propertyUseConst.HEALTHCARE];
    return commercialUses.includes(use);
  },

  // Particular types for the Income and Expenses type. Defined by business in User Story 1331443: Create I&E Comps Table - TREPP Comps
  isMultifamilyIncomeExpensesType(use) {
    const multifamilyIncomeExpensesUses = [propertyUseConst.MULTIHOUSING, propertyUseConst.RESIDENTIAL];
    return multifamilyIncomeExpensesUses.includes(use);
  },
  isCommercialIncomeExpensesType(use) {
    const commercialIncomeExpensesUses = [propertyUseConst.RETAIL, propertyUseConst.INDUSTRIAL, propertyUseConst.OFFICE, propertyUseConst.SPECIAL, propertyUseConst.LAND, propertyUseConst.HOSPITALITY, propertyUseConst.HEALTHCARE];
    return commercialIncomeExpensesUses.includes(use);
  },

  /**
   * https://gist.github.com/Yimiprod/7ee176597fef230d1451
   * Deep diff between two object, using lodash
   * @param  {Object} object Object compared
   * @param  {Object} base   Object to compare with
   * @return {Object}        Return a new object who represent the diff
   */
  difference(object, base) {
    const changes = function (object, base) {
      return _.transform(object, function (result, value, key) {
        if (!_.isEqual(value, base[key])) {
          result[key] = _.isObject(value) && _.isObject(base[key]) ? changes(value, base[key]) : value;
        }
      });
    };

    return changes(object, base);
  },

  getPropertyDifferences(mutatedProperty, originalProperty) {
    return this.difference(mutatedProperty, originalProperty);
  },

  /**
   * Change to blob at string in base64
   * @param {*} b64Data string in base 64
   * @param {*} contentType content type "applicantion/json, image/pdf etc"
   * @returns blob type with size & name.
   */
  b64toBlob(b64Data, contentType = '', sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i += 1) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  },

  sortByNumOrder(a, b) {
    if (a.NumOrder < b.NumOrder) {
      return -1;
    }
    if (a.NumOrder > b.NumOrder) {
      return 1;
    }
    return 0;
  },
};
